import * as InFlowEnums from "ipctg-inflow-types/InFlowEnums"

import { startCase } from "./etc"
import { TranslationKey } from "./types/local"

export class ExpectedError extends Error {
  constructor(
    failReason: string,
    translationKey: TranslationKey<"errors">,
    name: string
  ) {
    super(translationKey)
    this.translationKey = translationKey
    this.name = name
    this.failReason = failReason
    Object.setPrototypeOf(this, ExpectedError.prototype)
  }
  failReason: string
  translationKey: TranslationKey<"errors">
}

export class StripeLoadError extends ExpectedError {
  constructor() {
    super("stripeLoadError", "common.stripeLoadError", "StripeLoadError")
  }
}

export class StripeTimeoutError extends ExpectedError {
  constructor() {
    super(
      "stripeTimeoutError",
      "common.stripeTimeoutError",
      "StripeTimeoutError"
    )
  }
}

export class GatedAccountError extends ExpectedError {
  constructor(failReason: InFlowEnums.AccountFailReason) {
    const key = toLowerFirstKey(InFlowEnums.AccountFailReason, failReason)
    super(failReason, `account.${key}`, startCase(failReason))
  }
}

export class GatedAppUserError extends ExpectedError {
  constructor(failReason: InFlowEnums.AppUserPUTFailReason) {
    const key = toLowerFirstKey(InFlowEnums.AppUserPUTFailReason, failReason)
    super(failReason, `appUser.${key}`, startCase(failReason))
  }
}

export class GatedAutoPaySelectCardError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedAutoPaySelectCardFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedAutoPaySelectCardFailReason,
      failReason
    )
    super(failReason, `autoPaySelectCard.${key}`, startCase(failReason))
  }
}

export class GatedWelcomeError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedWelcomeFailReason) {
    const key = toLowerFirstKey(InFlowEnums.GatedWelcomeFailReason, failReason)
    super(failReason, `welcome.${key}`, startCase(failReason))
  }
}

export class ParkWelcomeError extends ExpectedError {
  constructor(failReason: InFlowEnums.WelcomeFailReason) {
    const key = toLowerFirstKey(InFlowEnums.WelcomeFailReason, failReason)
    super(failReason, `welcome.${key}`, startCase(failReason))
  }
}

export class GatedCheckoutError extends ExpectedError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    const key = toLowerFirstKey(InFlowEnums.CheckoutFailReason, failReason)
    super(failReason, `gatedCheckoutError.${key}`, startCase(failReason))
  }
}

export class GatedConfirmSetupIntentError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedConfirmSetupIntentFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedConfirmSetupIntentFailReason,
      failReason
    )
    super(
      failReason,
      `gatedConfirmSetupIntentError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedConfirmSetupIntentWalletError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedConfirmSetupIntentWalletFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedConfirmSetupIntentWalletFailReason,
      failReason
    )
    super(
      failReason,
      `gatedConfirmSetupIntentWalletError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedEmailConfirmationError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedEmailConfirmationFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedEmailConfirmationFailReason,
      failReason
    )
    super(
      failReason,
      `gatedEmailConfirmationError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedEmailReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedEmailReceiptFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedEmailReceiptFailReason,
      failReason
    )
    super(failReason, `gatedEmailReceiptError.${key}`, startCase(failReason))
  }
}

// Not gated, but GatedEmailReceiptFailReason is a super set of this
export class ParkEmailReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.EmailFailReason) {
    const key = toLowerFirstKey(InFlowEnums.EmailFailReason, failReason)
    super(failReason, `gatedEmailReceiptError.${key}`, startCase(failReason))
  }
}

export class GatedGetConfirmationDetailsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedGetConfirmationDetailsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedGetConfirmationDetailsFailReason,
      failReason
    )
    super(
      failReason,
      `gatedGetConfirmationDetailsError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedLookupSessionsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedLookupSessionsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedLookupSessionsFailReason,
      failReason
    )
    super(failReason, `gatedLookupSessionsError.${key}`, startCase(failReason))
  }
}

export class GatedRegisterSessionError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedRegisterSessionFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedRegisterSessionFailReason,
      failReason
    )
    super(failReason, `gatedRegisterSessionError.${key}`, startCase(failReason))
  }
}

export class GatedPayError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class GatedPaymentIntentError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class GatedPromoPaymentError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedPromoPaymentFailReasonType) {
    super(
      failReason,
      `gatedPromoPaymentError.${uncapitalize(failReason)}`,
      startCase(failReason)
    )
  }
}

export class GatedPromoRegisterError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedPromoRegisterFailReasonType) {
    super(
      failReason,
      `gatedPromoRegisterError.${uncapitalize(failReason)}`,
      startCase(failReason)
    )
  }
}

export class GatedReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedReceiptFailReason) {
    const key = toLowerFirstKey(InFlowEnums.GatedReceiptFailReason, failReason)
    super(failReason, `gatedReceiptError.${key}`, startCase(failReason))
  }
}

// Not gated, but GatedReceiptFailReason is a super set of this
export class ParkReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReceiptFailReason) {
    const key = toLowerFirstKey(InFlowEnums.ReceiptFailReason, failReason)
    super(failReason, `gatedReceiptError.${key}`, startCase(failReason))
  }
}

export class GatedSavedPaymentMethodActionError extends ExpectedError {
  constructor(
    failReason: InFlowEnums.GatedSavedPaymentMethodActionsFailReason
  ) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSavedPaymentMethodActionsFailReason,
      failReason
    )
    super(
      failReason,
      `savedPaymentMethodActionError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedSavedPaymentMethodsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSavedPaymentMethodsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSavedPaymentMethodsFailReason,
      failReason
    )
    super(failReason, `savedPaymentMethodsError.${key}`, startCase(failReason))
  }
}

export class GatedSessionDetailsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSessionDetailsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSessionDetailsFailReason,
      failReason
    )
    super(failReason, `gatedSessionDetailsError.${key}`, startCase(failReason))
  }
}

export class GatedSetupIntentError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSetupIntentFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSetupIntentFailReason,
      failReason
    )
    super(failReason, `gatedSetupIntentError.${key}`, startCase(failReason))
  }
}

export class GatedSetupIntentWalletError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSetupIntentWalletFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSetupIntentWalletFailReason,
      failReason
    )
    super(
      failReason,
      `gatedSetupIntentWalletError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedTagCashError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedTagCashFailReason) {
    const key = toLowerFirstKey(InFlowEnums.GatedTagCashFailReason, failReason)
    super(failReason, `gatedTagCashError.${key}`, startCase(failReason))
  }
}

export class GatedTextConfirmationError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedTextConfirmationFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedTextConfirmationFailReason,
      failReason
    )
    super(
      failReason,
      `gatedTextConfirmationError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedTextReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedTextReceiptFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedTextReceiptFailReason,
      failReason
    )
    super(failReason, `gatedTextReceiptError.${key}`, startCase(failReason))
  }
}

export class ParkTextReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.TextFailReason) {
    const key = toLowerFirstKey(InFlowEnums.TextFailReason, failReason)
    super(failReason, `gatedTextReceiptError.${key}`, startCase(failReason))
  }
}

export class ParkSessionsError extends ExpectedError {
  constructor(failReason: InFlowEnums.SessionsFailReason) {
    const key = toLowerFirstKey(InFlowEnums.SessionsFailReason, failReason)
    super(failReason, `parkSessionsError.${key}`, startCase(failReason))
  }
}

export class ParkValidPassesError extends ExpectedError {
  constructor(failReason: InFlowEnums.ValidPassesFailReason) {
    const key = toLowerFirstKey(InFlowEnums.ValidPassesFailReason, failReason)
    super(failReason, `parkValidPassesError.${key}`, startCase(failReason))
  }
}

export class ParkAccountError extends ExpectedError {
  constructor(failReason: InFlowEnums.AccountFailReason) {
    const key = toLowerFirstKey(InFlowEnums.AccountFailReason, failReason)
    super(failReason, `parkAccountError.${key}`, startCase(failReason))
  }
}

export class ParkPlateError extends ExpectedError {
  constructor(failReason: InFlowEnums.PlateFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PlateFailReason, failReason)
    super(failReason, `parkPlateError.${key}`, startCase(failReason))
  }
}

export class ParkRatesError extends ExpectedError {
  constructor(failReason: InFlowEnums.RatesFailReason) {
    const key = toLowerFirstKey(InFlowEnums.RatesFailReason, failReason)
    super(failReason, `parkRatesError.${key}`, startCase(failReason))
  }
}

export class ParkRatePassphraseError extends ExpectedError {
  constructor(failReason: InFlowEnums.RatePassphraseFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.RatePassphraseFailReason,
      failReason
    )
    super(failReason, `parkRatePassphraseError.${key}`, startCase(failReason))
  }
}

export class ParkCheckoutError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class ParkPromoError extends ExpectedError {
  constructor(failReason: InFlowEnums.PromoFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PromoFailReason, failReason)
    super(failReason, `parkPromoError.${key}`, startCase(failReason))
  }
}

export class ParkPaymentIntentError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class ParkPaymentIntentElementsError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class ParkPayError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class ParkPassesForSaleError extends ExpectedError {
  constructor(failReason: InFlowEnums.PassesForSaleFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PassesForSaleFailReason, failReason)
    super(failReason, `parkPassesForSaleError.${key}`, startCase(failReason))
  }
}

export class ParkPassesError extends ExpectedError {
  constructor(failReason: InFlowEnums.PassesFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PassesFailReason, failReason)
    super(failReason, `parkPassesError.${key}`, startCase(failReason))
  }
}

export class ParkPurchasePassError extends ExpectedError {
  constructor(failReason: InFlowEnums.PurchasePassFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PurchasePassFailReason, failReason)
    super(failReason, `parkPurchasePassError.${key}`, startCase(failReason))
  }
}

export class ParkPassRedeemError extends ExpectedError {
  constructor(failReason: InFlowEnums.PassRedeemFailReasonType) {
    super(
      failReason,
      `parkPassRedeemError.${uncapitalize(failReason)}`,
      startCase(failReason)
    )
  }
}

export class ParkPlateUpdateError extends ExpectedError {
  constructor(failReason: InFlowEnums.PlateUpdateFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PlateUpdateFailReason, failReason)
    super(failReason, `parkPlateUpdateError.${key}`, startCase(failReason))
  }
}

export class ParkAppUserPOSTError extends ExpectedError {
  constructor(failReason: InFlowEnums.AppUserPOSTFailReason) {
    const key = toLowerFirstKey(InFlowEnums.AppUserPOSTFailReason, failReason)
    super(failReason, `parkAppUserPOSTError.${key}`, startCase(failReason))
  }
}

export class ParkAppUserPUTError extends ExpectedError {
  constructor(failReason: InFlowEnums.AppUserPUTFailReason) {
    const key = toLowerFirstKey(InFlowEnums.AppUserPUTFailReason, failReason)
    super(failReason, `parkAppUserPUTError.${key}`, startCase(failReason))
  }
}

export class ParkSessionStatusError extends ExpectedError {
  constructor(failReason: InFlowEnums.ParkSessionStatusFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ParkSessionStatusFailReason,
      failReason
    )
    super(failReason, `parkSessionStatusError.${key}`, startCase(failReason))
  }
}

export class ParkRateEndTimeError extends ExpectedError {
  constructor(failReason: InFlowEnums.RateEndTimeFailReason) {
    const key = toLowerFirstKey(InFlowEnums.RateEndTimeFailReason, failReason)
    super(failReason, `parkRateEndTimeError.${key}`, startCase(failReason))
  }
}

export class ParkCalculateRateError extends ExpectedError {
  constructor(failReason: InFlowEnums.ParkCalculateRateFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ParkCalculateRateFailReason,
      failReason
    )
    super(failReason, `parkCalculateRateError.${key}`, startCase(failReason))
  }
}

export class ReservePromoError extends ExpectedError {
  constructor(failReason: InFlowEnums.PromoFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PromoFailReason, failReason)
    super(failReason, `parkPromoError.${key}`, startCase(failReason))
  }
}

export class AccessCodeError extends ExpectedError {
  constructor(failReason: InFlowEnums.PromoFailReason) {
    const key = toLowerFirstKey(InFlowEnums.PromoFailReason, failReason)
    super(failReason, `accessCodeError.${key}`, startCase(failReason))
  }
}

export class ReservePaymentIntentError extends ExpectedError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    const key = toLowerFirstKey(InFlowEnums.CheckoutFailReason, failReason)
    super(failReason, `gatedCheckoutError.${key}`, startCase(failReason))
  }
}

export class ReserveConfirmPaymentError extends ExpectedError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    const key = toLowerFirstKey(InFlowEnums.CheckoutFailReason, failReason)
    super(failReason, `gatedCheckoutError.${key}`, startCase(failReason))
  }
}

export class ReserveSetupIntentError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveSetupIntentFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveSetupIntentFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveConfirmSetupIntentError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveConfirmSetupIntentFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveConfirmSetupIntentFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveWelcomeError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveWelcomeFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveWelcomeFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveCalendarsError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveCalendarsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveCalendarsFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveCalendarAvailabilityError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveCalendarAvailabilityFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveCalendarAvailabilityFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveCalendarRatesError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveCalendarRatesFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveCalendarRatesFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveSelectRateError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveSelectRateFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveSelectRateFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReservePlateError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReservePlateFailReason) {
    const key = toLowerFirstKey(InFlowEnums.ReservePlateFailReason, failReason)
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveCheckoutError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveCheckoutFailReasonType) {
    super(
      failReason,
      `reserveCheckoutError.${uncapitalize(failReason)}`,
      startCase(failReason)
    )
  }
}

export class ReserveCurrentReservationsError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveCurrentReservationsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveCurrentReservationsFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class MembershipsError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveMembershipsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveMembershipsFailReason,
      failReason
    )
    super(failReason, `memberships.${key}`, startCase(failReason))
  }
}

export class ReservePastReservationsError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReservePastReservationsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReservePastReservationsFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReservePlateUpdateError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReservePlateUpdateFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReservePlateUpdateFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class ReserveCancelReservationError extends ExpectedError {
  constructor(failReason: InFlowEnums.ReserveCancelReservationFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.ReserveCancelReservationFailReason,
      failReason
    )
    super(failReason, `reserveError.${key}`, startCase(failReason))
  }
}

export class GatedThemeError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedThemeFailReason) {
    const key = toLowerFirstKey(InFlowEnums.GatedThemeFailReason, failReason)
    super(failReason, `gatedThemeError.${key}`, startCase(failReason))
  }
}

export class ReserveDateInPastError extends ExpectedError {
  constructor() {
    super("dateInPast", "reserve.dateInPast", "ReserveDateInPastError")
  }
}

type PascalToLowerFirst<S extends string> =
  S extends `${infer First}${infer Rest}` ? `${Lowercase<First>}${Rest}` : S

// Type to get the key name from an enum value
type ValueToKey<T extends Record<string, string>, V extends T[keyof T]> = {
  [K in keyof T]: T[K] extends V ? K : never
}[keyof T]

// Type to convert enum value to lowerFirst key name
type EnumValueToLowerFirstKey<
  T extends Record<string, string>,
  V extends T[keyof T],
> = PascalToLowerFirst<
  ValueToKey<T, V> extends string ? ValueToKey<T, V> : never
>

export function uncapitalize<T extends string>(str: T): Uncapitalize<T> {
  return (str.charAt(0).toLowerCase() + str.slice(1)) as Uncapitalize<T>
}

const toLowerFirstKey = <
  T extends Record<string, string>,
  V extends T[keyof T],
>(
  enumObj: T,
  value: V
): EnumValueToLowerFirstKey<T, V> => {
  const key = Object.keys(enumObj).find((k) => enumObj[k] === value)
  return (key![0].toLowerCase() + key!.slice(1)) as EnumValueToLowerFirstKey<
    T,
    V
  >
}
